/**
 * Software-quote funnel.
 *
 * Maps knowledge-base articles to a product in the software store
 * (/software/{slug}) that we can actually quote through distribution.
 *
 * WHY THIS EXISTS
 * The KB is organised by vendor and the store is stocked with those same
 * vendors, but nothing linked the two. Measured over 88 days, KB categories
 * that map to a quotable product drew 638,812 impressions and 6,401 clicks
 * (~2,134 visits/month) while the store had received a single inquiry in its
 * lifetime. CrowdStrike alone is 4,658 clicks a quarter across 35 articles
 * ranking at positions 6-8.
 *
 * The economics are not close. Those visits are worth roughly $90/year as ad
 * impressions at a $4.10 page RPM. One CrowdStrike Falcon Enterprise deal
 * (100 devices at $184.99/device/yr list) is an $18,499 contract. So on these
 * pages the quote beats the ad by one to two orders of magnitude, which is why
 * they are funnel pages rather than ads pages.
 *
 * INTENT MATTERS MORE THAN VENDOR
 * A reader of "how to configure CrowdStrike exclusions for SQL Server" already
 * owns CrowdStrike — they are a renewal/expansion prospect at best. A reader of
 * "how to uninstall the CrowdStrike Falcon sensor" is leaving, which is a
 * switching signal and the strongest buying intent in the whole set. Those get
 * pointed at alternatives rather than at the product they are removing.
 */

/** KB vendor category -> the store product a reader of that category would buy. */
const CATEGORY_TO_PRODUCT: Record<string, string> = {
  crowdstrike: 'crowdstrike-falcon',
  defender: 'defender-for-endpoint',
  '1password': '1password',
  acronis: 'acronis-cyber-protect',
  proofpoint: 'proofpoint-essentials',
  intune: 'microsoft-intune',
  'microsoft-365': 'microsoft-365',
  checkpoint: 'check-point-harmony-email',
};

/**
 * Articles whose intent is "I am removing this product". The reader is churning,
 * so offering them the thing they are uninstalling is worse than useless —
 * point at the alternative we can also quote.
 */
const SWITCHING_INTENT: Record<string, string> = {
  'crowdstrike/how-to-uninstall-crowdstrike-falcon-sensor': 'sentinelone',
  'acronis/how-to-uninstall-acronis-cyber-protect-cloud': 'veeam',
};

/*
 * Note on what is deliberately NOT here: "how to enable uninstall protection"
 * reads like churn to a keyword match but is the opposite signal — that reader
 * is hardening an install they own and intend to keep. They get the normal
 * category mapping, not a competitor pitch.
 */

/** Display names, mirrored from store_product_families. KB pages are
 *  force-static so the name cannot be looked up from D1 at render time. */
const PRODUCT_NAMES: Record<string, string> = {
  'crowdstrike-falcon': 'CrowdStrike Falcon',
  'defender-for-endpoint': 'Microsoft Defender for Endpoint',
  '1password': '1Password',
  'acronis-cyber-protect': 'Acronis Cyber Protect Cloud',
  'proofpoint-essentials': 'Proofpoint Essentials',
  'microsoft-intune': 'Microsoft Intune',
  'microsoft-365': 'Microsoft 365',
  'check-point-harmony-email': 'Harmony Email & Collaboration',
  sentinelone: 'SentinelOne Singularity',
  veeam: 'Veeam Data Platform',
};

/** Vendor a category's readers are currently operating, for switching copy. */
const CATEGORY_VENDOR: Record<string, string> = {
  crowdstrike: 'CrowdStrike',
  defender: 'Microsoft Defender',
  proofpoint: 'Proofpoint',
  acronis: 'Acronis',
  checkpoint: 'Check Point',
};

export interface SoftwareQuoteTarget {
  /** Store product slug — resolves to /software/{slug}. */
  productSlug: string;
  /** Display name for the CTA. */
  productName: string;
  /** True when the reader is leaving a product rather than operating it. */
  switching: boolean;
  /** The product being left, when switching. */
  leavingProduct?: string;
}

/**
 * Resolve the quote target for a KB article, or undefined when the article
 * isn't about something we can quote.
 */
export function getSoftwareQuoteTargetForKB(
  category: string,
  slug: string
): SoftwareQuoteTarget | undefined {
  const key = `${category}/${slug}`;

  const switchTo = SWITCHING_INTENT[key];
  if (switchTo) {
    return {
      productSlug: switchTo,
      productName: PRODUCT_NAMES[switchTo] ?? switchTo,
      switching: true,
      leavingProduct: CATEGORY_VENDOR[category],
    };
  }

  const productSlug = CATEGORY_TO_PRODUCT[category];
  if (!productSlug) return undefined;

  return {
    productSlug,
    productName: PRODUCT_NAMES[productSlug] ?? productSlug,
    switching: false,
  };
}

/** True when this KB article belongs to the software-quote funnel. */
export function isSoftwareQuoteFunnelKB(category: string, slug: string): boolean {
  return getSoftwareQuoteTargetForKB(category, slug) !== undefined;
}
